import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPageData } from "../../../redux/pageSlice";
import "./MilkysFarm.css";
import { useVisibilityOnScroll } from "../../../Component/Hooks/useVisibilityOnScroll/useVisibilityOnScroll";
import VisibilitySection from "../../../Component/Hooks/useVisibilityOnScroll/VisibilitySection";
import TC3L from "../../../Component/ReuseComponent/TCLines/TC3L";
import HomeS2PBImg from "../../../Assets/Svg/Home/HomeS2PBImg.svg";
import MilkysFarmS1P2Img from "../../../Assets/webp/MilkyFarm/MilkysFarmS1P2Img.webp";
import MilkysFarmS1P2ImgMob from "../../../Assets/webp/MilkyFarm/MilkysFarmS1P2ImgMob.webp";
import MFS2P2C3Img from "../../../Assets/Svg/MilkysFarm/MFS2P2C3.svg";
import { LoaderWrap } from "../../../Component/GlobalLoader/LoaderWrap";

export default function MilkysFarmContent() {
  const { sectionsRef, visibilityStates } = useVisibilityOnScroll();
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.page);

  useEffect(() => {
    dispatch(fetchPageData({ page_id: 7, lang: "en" }));
  }, [dispatch]);

  const section1 = data.find((s) => s.section_id === "1");
  const section2 = data.find((s) => s.section_id === "2");
  return (
    <>
      <LoaderWrap>
        <div className="MilkysFarmContainer">
          <section>
            <MilkysFarmS1 section1={section1} contents={section1?.contents} />
          </section>
          <VisibilitySection index={1} sectionsRef={sectionsRef} visibilityStates={visibilityStates} className="MilkysFarmS2">
            <MilkysFarmS2 section2={section2} contents={section2?.contents} />
          </VisibilitySection>
        </div>
      </LoaderWrap>
    </>
  );
}
function MilkysFarmS1({ section1, contents }) {
  const msSectionRef = useRef(null);
  const [msVisibleBoxes, setMsVisibleBoxes] = useState([false, false, false]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    const handleScroll = () => {
      if (isAnimating) return;
      if (msSectionRef.current) {
        const sectionTop = msSectionRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;

        const progress = Math.max(0, Math.min(100, ((windowHeight - sectionTop) / windowHeight) * 100));

        if (progress >= 12 && !hasAnimated) {
          setIsAnimating(true);
          setMsVisibleBoxes([false, false, false]);
          setTimeout(() => {
            setMsVisibleBoxes([true, false, false]);
            setTimeout(() => {
              setMsVisibleBoxes([true, true, false]);
              setTimeout(() => {
                setMsVisibleBoxes([true, true, true]);
                setHasAnimated(true);
                setIsAnimating(false);
              }, 300);
            }, 300);
          }, 500);
        }

        if (progress < 12 && hasAnimated) {
          setMsVisibleBoxes([false, false, false]);
          setHasAnimated(false);
        }
      }
    };

    const onScroll = () => requestAnimationFrame(handleScroll);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [hasAnimated, isAnimating]);

  return (
    <>
      <div className="MilkysFarmS1">
        <TC3L Head="Milky's Farm" Title={section1?.title} TitleWidthMob="85vw" Para={section1?.description} ParaWidth="55vw" ParaWidthMob="84vw" />
        <div className="MFS1P2">
          <div className="MFS1P2P1 TFut16PxPCUC">
            <a href="https://maps.app.goo.gl/wUtjuPkitULYsWdU7" target="blank">
              Visit our farm <img src={HomeS2PBImg} alt="" />
            </a>
            <img src={isMobile ? MilkysFarmS1P2ImgMob : MilkysFarmS1P2Img} alt="" className="MFS1P2Img" />
          </div>
          <div className="MFS1P2P2" ref={msSectionRef}>
            {contents?.map((item, index) => (
              <MFS1P2P2Box key={item.id} Number={item.title} Text={item.description} isVisible={msVisibleBoxes[index]} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function MFS1P2P2Box({ Number, Text, isVisible }) {
  return (
    <>
      <div className={`MFS1P2P2Box ${isVisible ? "visible" : "hidden"}`}>
        <div className="MFS1P2P2BoxN TGLo48PxPC">{Number}</div>
        <div className="MFS1P2P2BoxT TFut24PxPC">{Text}</div>
      </div>
    </>
  );
}

function MilkysFarmS2({ section2, contents }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const imgC = MFS2P2C3Img;

  const boxes = Array.isArray(contents)
    ? contents.map((item) => ({
        number: parseInt(item.title),
        imgL: item.image,
        imgC: imgC,
        text: item.description,
      }))
    : [];

  return isMobile ? <FarmS2MobComponent section2={section2} boxes={boxes} /> : <FarmS2WebComponent section2={section2} boxes={boxes} />;
}

function FarmS2WebComponent({ section2, boxes }) {
  const sectionsRef = useRef([]);
  const [scrollProgress, setScrollProgress] = useState([]);
  const [startAnimation, setStartAnimation] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStartAnimation(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => sectionRef.current && observer.unobserve(sectionRef.current);
  }, []);

  useEffect(() => {
    if (!startAnimation) return;

    const handleScroll = () => {
      const newProgress = sectionsRef.current.map((el) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const boxHeight = el.offsetHeight;

          let progress = Math.min(1, Math.max(0, 1 - rect.top / boxHeight));
          return progress;
        }
        return 0;
      });

      setScrollProgress(newProgress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [startAnimation]);

  const isVisible = (index) => {
    if (index === 0) return scrollProgress[0] > 0;
    return scrollProgress[index - 1] >= 1;
  };

  return (
    <>
      <div ref={sectionRef} className="MilkysFarmS3">
        <div className="MFS2P1">
          <TC3L Title={section2?.title} Para={section2?.description} ParaWidth="56vw" ParaWidthMob="89vw" />
        </div>
        <div className="MFS2P2">
          {boxes.map((box, index) => {
            const progress = scrollProgress[index] || 0;
            const elementVisible = isVisible(index);

            return (
              <div key={index} ref={(el) => (sectionsRef.current[index] = el)} className="MFS2P2Box">
                {/* Left Box */}
                <div className={`MFS2P2BoxL ${elementVisible ? "visible" : ""}`}>
                  <img src={box.imgL} alt="" className="MFS2P2BoxLImg" />
                </div>

                {/* Center Box */}
                <div className="MFS2P2BoxC">
                  <div
                    className="MFSP2BoxCContainer"
                    style={{
                      opacity: elementVisible ? "1" : "0.5",
                      transition: "opacity 0.6s ease",
                    }}
                  >
                    <img src={box.imgC} alt="" className="MFS2P2BoxCImg" />
                    <div className={`MFS2P2BoxCT TGLo48PxPC MFS2P2BoxCT-${box.number}`}>{box.number}</div>
                  </div>
                  <div
                    className="MFS2P2Line"
                    style={{
                      background: elementVisible ? `linear-gradient(to bottom, #90CBFC ${progress * 100}%, #D9EDFF ${progress * 100}%)` : `#D9EDFF`,
                    }}
                  ></div>
                </div>

                {/* Right Box */}
                <div className={`MFS2P2BoxR TGLo32PxPC ${elementVisible ? "visible" : ""}`}>{box.text}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function FarmS2MobComponent({ section2, boxes }) {
  const sectionsRef = useRef([]);
  const [scrollProgress, setScrollProgress] = useState([]);
  const [startAnimation, setStartAnimation] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStartAnimation(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => sectionRef.current && observer.unobserve(sectionRef.current);
  }, []);

  useEffect(() => {
    if (!startAnimation) return;

    const handleScroll = () => {
      const newProgress = sectionsRef.current.map((el) => {
        if (el) {
          const rect = el.getBoundingClientRect();
          const boxHeight = el.offsetHeight;

          let progress = Math.min(1, Math.max(0, 1 - rect.top / boxHeight));
          return progress;
        }
        return 0;
      });

      setScrollProgress(newProgress);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [startAnimation]);

  const isVisible = (index) => {
    if (index === 0) return scrollProgress[0] > 0;
    return scrollProgress[index - 1] >= 1;
  };

  return (
    <>
      <div ref={sectionRef} className="MilkysFarmS3">
        <div className="MFS2P1">
          <TC3L Head="The Lifecycle of Milk" Title={section2?.title} Para={section2?.description} ParaWidth="56vw" ParaWidthMob="89vw" />
        </div>
        <div className="MFS2P2">
          {boxes.map((box, index) => {
            const progress = scrollProgress[index] || 0;
            const elementVisible = isVisible(index);

            return (
              <div key={index} ref={(el) => (sectionsRef.current[index] = el)} className="MFS2P2Box">
                {/* Top Box */}
                <div className="MFS2P2BoxC">
                  <div
                    className="MFSP2BoxCContainer"
                    style={{
                      opacity: elementVisible ? "1" : "0.5",
                      transition: "opacity 0.6s ease",
                    }}
                  >
                    <img src={box.imgC} alt="" className="MFS2P2BoxCImg" />
                    <div className={`MFS2P2BoxCT TGLo48PxPC MFS2P2BoxCT-${box.number}`}>{box.number}</div>
                  </div>
                </div>
                {/* Center */}
                <div>
                  <div className={`MFS2P2BoxL ${elementVisible ? "visible" : ""}`}>
                    <img src={box.imgL} alt="" className="MFS2P2BoxLImg" />
                  </div>
                  <div className={`MFS2P2BoxR TGLo32PxPC ${elementVisible ? "visible" : ""}`}>{box.text}</div>
                </div>
                {/* End */}
                <div
                  className="MFS2P2Line"
                  style={{
                    background: elementVisible ? `linear-gradient(to bottom, #90CBFC ${progress * 100}%, #D9EDFF ${progress * 100}%)` : `#D9EDFF`,
                  }}
                ></div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
